/**
 * Google Maps Service
 * Provides geocoding, reverse geocoding, and location utilities
 */

import { getConfigValue } from './systemConfigurationService';

const ENV_API_KEY = import.meta.env?.VITE_GOOGLE_MAPS_API_KEY;
let cachedApiKey = null;

/**
 * Get Google Maps API key from database or environment variable
 * Priority: Database > Environment Variable
 */
const getApiKey = async () => {
  // Return cached key if available
  if (cachedApiKey) {
    return cachedApiKey;
  }

  try {
    // Try to get from database first
    const dbApiKey = await getConfigValue('google_maps_api_key');
    
    if (dbApiKey && dbApiKey?.trim() !== '') {
      cachedApiKey = dbApiKey;
      return dbApiKey;
    }
  } catch (error) {
    console.warn('Could not fetch API key from database, falling back to environment variable:', error);
  }

  // Fallback to environment variable
  if (ENV_API_KEY && ENV_API_KEY !== 'your-google-maps-api-key-here') {
    cachedApiKey = ENV_API_KEY;
    return ENV_API_KEY;
  }

  return null;
};

/**
 * Clear cached API key (useful when updating configuration)
 */
export const clearApiKeyCache = () => {
  cachedApiKey = null;
};

/**
 * Check if Google Maps API key is configured
 */
export const isGoogleMapsConfigured = async () => {
  const apiKey = await getApiKey();
  return apiKey !== null;
};

/**
 * Geocode an address to coordinates
 * @param {string} address - Full address string
 * @returns {Promise<{lat: number, lng: number, formattedAddress: string}>}
 */
export const geocodeAddress = async (address) => {
  const GOOGLE_MAPS_API_KEY = await getApiKey();
  
  if (!GOOGLE_MAPS_API_KEY) {
    throw new Error('مفتاح Google Maps API غير متوفر');
  }

  if (!address || address?.trim() === '') {
    throw new Error('العنوان مطلوب');
  }

  try {
    const encodedAddress = encodeURIComponent(address);
    let url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${GOOGLE_MAPS_API_KEY}&language=ar`;
    
    const response = await fetch(url);
    const data = await response?.json();

    if (data?.status === 'OK' && data?.results?.length > 0) {
      const result = data?.results?.[0];
      return {
        lat: result?.geometry?.location?.lat,
        lng: result?.geometry?.location?.lng,
        formattedAddress: result?.formatted_address,
        placeId: result?.place_id,
        addressComponents: result?.address_components
      };
    } else if (data?.status === 'ZERO_RESULTS') {
      throw new Error('لم يتم العثور على نتائج للعنوان المدخل');
    } else if (data?.status === 'OVER_QUERY_LIMIT') {
      throw new Error('تم تجاوز حد الاستعلامات اليومي');
    } else if (data?.status === 'REQUEST_DENIED') {
      throw new Error('تم رفض الطلب - تحقق من مفتاح API');
    } else {
      throw new Error(`فشل في تحديد الموقع: ${data?.status}`);
    }
  } catch (error) {
    console.error('Geocoding error:', error);
    throw error;
  }
};

/**
 * Reverse geocode coordinates to address
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<{address: string, city: string, neighborhood: string}>}
 */
export const reverseGeocode = async (lat, lng) => {
  const GOOGLE_MAPS_API_KEY = await getApiKey();
  
  if (!GOOGLE_MAPS_API_KEY) {
    throw new Error('مفتاح Google Maps API غير متوفر');
  }

  if (!lat || !lng) {
    throw new Error('الإحداثيات مطلوبة');
  }

  try {
    let url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}&language=ar`;
    
    const response = await fetch(url);
    const data = await response?.json();

    if (data?.status === 'OK' && data?.results?.length > 0) {
      const result = data?.results?.[0];
      const addressComponents = result?.address_components || [];

      // Extract city and neighborhood from address components
      let city = '';
      let neighborhood = '';
      
      addressComponents?.forEach(component => {
        if (component?.types?.includes('locality')) {
          city = component?.long_name;
        }
        if (component?.types?.includes('sublocality') || component?.types?.includes('neighborhood')) {
          neighborhood = component?.long_name;
        }
      });

      return {
        address: result?.formatted_address,
        city: city,
        neighborhood: neighborhood,
        addressComponents: addressComponents,
        placeId: result?.place_id
      };
    } else {
      throw new Error(`فشل في تحديد العنوان: ${data?.status}`);
    }
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    throw error;
  }
};

/**
 * Get place details by place ID
 * @param {string} placeId - Google Place ID
 * @returns {Promise<Object>}
 */
export const getPlaceDetails = async (placeId) => {
  const GOOGLE_MAPS_API_KEY = await getApiKey();
  
  if (!GOOGLE_MAPS_API_KEY) {
    throw new Error('مفتاح Google Maps API غير متوفر');
  }

  if (!placeId) {
    throw new Error('معرف المكان مطلوب');
  }

  try {
    let url = `https://maps.googleapis.com/maps/api/place/details/json?place_id=${placeId}&key=${GOOGLE_MAPS_API_KEY}&language=ar`;
    
    const response = await fetch(url);
    const data = await response?.json();

    if (data?.status === 'OK') {
      return data?.result;
    } else {
      throw new Error(`فشل في تحميل تفاصيل المكان: ${data?.status}`);
    }
  } catch (error) {
    console.error('Place details error:', error);
    throw error;
  }
};

/**
 * Calculate distance between two coordinates (Haversine formula)
 * @param {number} lat1 - First latitude
 * @param {number} lng1 - First longitude
 * @param {number} lat2 - Second latitude
 * @param {number} lng2 - Second longitude
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return Math.round(distance * 100) / 100; // Round to 2 decimal places
};

/**
 * Get current user location using browser geolocation
 * @returns {Promise<{lat: number, lng: number}>}
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator?.geolocation) {
      reject(new Error('المتصفح لا يدعم تحديد الموقع'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position?.coords?.latitude,
          lng: position?.coords?.longitude
        });
      },
      (error) => {
        let errorMessage = 'فشل في تحديد الموقع الحالي';
        
        switch (error?.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'تم رفض إذن الوصول للموقع';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'معلومات الموقع غير متوفرة';
            break;
          case error.TIMEOUT:
            errorMessage = 'انتهت مهلة طلب الموقع';
            break;
        }
        
        reject(new Error(errorMessage));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
};

/**
 * Search for places by query
 * @param {string} query - Search query
 * @param {Object} location - Optional center location {lat, lng}
 * @param {number} radius - Search radius in meters (default: 5000)
 * @returns {Promise<Array>}
 */
export const searchPlaces = async (query, location = null, radius = 5000) => {
  const GOOGLE_MAPS_API_KEY = await getApiKey();
  
  if (!GOOGLE_MAPS_API_KEY) {
    throw new Error('مفتاح Google Maps API غير متوفر');
  }

  if (!query || query?.trim() === '') {
    throw new Error('نص البحث مطلوب');
  }

  try {
    let url = `https://maps.googleapis.com/maps/api/place/textsearch/json?query=${encodeURIComponent(query)}&key=${GOOGLE_MAPS_API_KEY}&language=ar`;
    
    if (location?.lat && location?.lng) {
      url += `&location=${location?.lat},${location?.lng}&radius=${radius}`;
    }
    
    const response = await fetch(url);
    const data = await response?.json();

    if (data?.status === 'OK') {
      return data?.results?.map(place => ({
        placeId: place?.place_id,
        name: place?.name,
        address: place?.formatted_address,
        lat: place?.geometry?.location?.lat,
        lng: place?.geometry?.location?.lng,
        types: place?.types,
        rating: place?.rating
      }));
    } else if (data?.status === 'ZERO_RESULTS') {
      return [];
    } else {
      throw new Error(`فشل في البحث: ${data?.status}`);
    }
  } catch (error) {
    console.error('Place search error:', error);
    throw error;
  }
};

export default {
  isGoogleMapsConfigured,
  geocodeAddress,
  reverseGeocode,
  getPlaceDetails,
  calculateDistance,
  getCurrentLocation,
  searchPlaces
};
